<script setup lang="ts">
/**
 * The one dialog that stands between a click and a spend.
 *
 * Rendered once, near the root, and driven entirely by `state/confirm-purchase`
 * — it has no props and knows nothing about the shop. That is what lets a sixth
 * kind of purchase inherit it by calling one function.
 *
 * Three things about it are load-bearing:
 *
 *   **Escape and the backdrop both decline.** The safe answer has to be the easy
 *   one, and on a modal about spending money the safe answer is no.
 *
 *   **Focus moves to the dialog and the confirm button is not autofocused.**
 *   Autofocusing it would let a stray Return key spend credits — the exact
 *   accident this exists to prevent — so the *cancel* button takes focus and
 *   Return is therefore harmless.
 *
 *   **The price is formatted here, not by the caller.** Every call site would
 *   otherwise carry its own idea of what a number of credits looks like, and one
 *   of them would eventually disagree.
 */
import { computed, nextTick, ref, watch } from 'vue';
import CurrencyIcon from '@/ui/CurrencyIcon.vue';
import {
  acceptPurchase,
  declinePurchase,
  purchaseRequest,
} from '@/state/confirm-purchase';
import { formatCredits } from '@/garage/stat-display';
import ItemPreview from '@/garage/ItemPreview.vue';
import StatList from '@/garage/StatList.vue';
import TitleBadge from '@/ui/TitleBadge.vue';
import { previewsAllowed } from '@/state/settings';

const cancelButton = ref<HTMLButtonElement | null>(null);

/**
 * A flat image that 404'd, so the dialog can fall back to no picture at all.
 *
 * Cleared whenever a new request opens, not accumulated per URL: this dialog
 * shows exactly one thing at a time, and a `Set` of dead URLs would be a cache
 * whose only reader is a modal that has already moved on. `ItemPreview` owns its
 * own failure state for the rendered stills, which is why only the `image` kind
 * needs this.
 */
const artFailed = ref(false);

/**
 * Whether to draw the art box at all.
 *
 * A `model` kind is dropped entirely when the player has switched previews off:
 * `ItemPreview` correctly fetches and renders nothing in that state, but what it
 * leaves is a *quiet plate* — right on a card, where the box is part of a fixed
 * grid, and wrong in a modal, where it would be a permanently empty rectangle
 * above the words. The rule this dialog follows is that a spend it cannot
 * illustrate falls back to the text-only shape, and "cannot" includes "the player
 * asked me not to".
 *
 * The flat-image and title kinds are unaffected: neither goes near the renderer,
 * and a setting about 3-D previews has no business hiding a pattern swatch or a
 * badge drawn in CSS.
 */
const showArt = computed(() => {
  const art = purchaseRequest.value?.art;
  if (!art) return false;
  if (art.kind === 'model') return previewsAllowed();
  if (art.kind === 'image') return !artFailed.value;
  return true;
});

/**
 * Whether the picture sits *beside* the words rather than above them.
 *
 * Every art kind that is a picture takes the two-column shape. Two do not, and
 * for opposite reasons:
 *
 *   **No art at all** — there is no left column to have, so the dialog keeps the
 *   narrow text-only proportions it has always had. This is the branch `showArt`
 *   already computes, and it covers a 3-D preview the player has switched off as
 *   well as a flat image that 404'd.
 *
 *   **A title badge** — its box deliberately has no aspect ratio (see `.art.badge`
 *   below): it is one short line of drawn text, and a 12.5rem column would squash
 *   it into a strip. A badge is closer to a word than to a photograph, so it stays
 *   over the sentence it belongs to.
 */
const sideBySide = computed(() => {
  const art = purchaseRequest.value?.art;
  return !!art && showArt.value && art.kind !== 'title';
});

/**
 * Focus follows the dialog opening.
 *
 * On the cancel button rather than on confirm: a dialog that opens with "yes"
 * focused turns a Return keypress into a purchase, which is the accident this
 * whole feature exists to catch.
 */
watch(purchaseRequest, async (request) => {
  if (!request) return;
  // Before the focus move, so a second dialog opening cannot inherit the first
  // one's dead image and draw nothing for a picture that exists.
  artFailed.value = false;
  await nextTick();
  cancelButton.value?.focus();
});

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    event.stopPropagation();
    declinePurchase();
  }
}
</script>

<template>
  <div
    v-if="purchaseRequest"
    class="scrim"
    data-test="confirm-purchase"
    @click.self="declinePurchase()"
    @keydown="onKeydown"
  >
    <div
      class="dialog"
      :class="{ wide: sideBySide }"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
    >
      <h2 id="confirm-title">Confirm purchase</h2>

      <div class="head">
        <div
          v-if="purchaseRequest.art && showArt"
          class="art"
          :class="{ badge: purchaseRequest.art.kind === 'title' }"
          data-test="confirm-art"
        >
          <ItemPreview
            v-if="purchaseRequest.art.kind === 'model'"
            :hull-id="purchaseRequest.art.hullId"
            :turret-id="purchaseRequest.art.turretId"
            :framing="purchaseRequest.art.framing"
            :label="purchaseRequest.item"
            eager
          />
          <div v-else-if="purchaseRequest.art.kind === 'title'" class="badge-art">
            <TitleBadge :title-id="purchaseRequest.art.titleId" variant="card" />
          </div>
          <img
            v-else-if="purchaseRequest.art.kind === 'image'"
            :src="purchaseRequest.art.src"
            :class="{ contain: purchaseRequest.art.contain }"
            alt=""
            @error="artFailed = true"
          />
        </div>

        <div class="says">
          <p class="question">
            {{ purchaseRequest.verb ?? 'Buy' }}
            <strong data-test="confirm-item">{{ purchaseRequest.item }}</strong>?
          </p>
          <p v-if="purchaseRequest.detail" class="detail">{{ purchaseRequest.detail }}</p>

          <p class="price" data-test="confirm-price">
            <span class="amount">
              {{
                purchaseRequest.priceLabel ??
                (purchaseRequest.currency === 'credits'
                  ? formatCredits(purchaseRequest.price)
                  : purchaseRequest.price)
              }}
            </span>
            <span v-if="!purchaseRequest.priceLabel" class="unit">
              <CurrencyIcon :kind="purchaseRequest.currency" />
            </span>
          </p>
        </div>
      </div>

      <div v-if="purchaseRequest.stats" class="delta" data-test="confirm-stats">
        <StatList
          :descriptors="purchaseRequest.stats.descriptors"
          :values="purchaseRequest.stats.values"
          :against="purchaseRequest.stats.against"
        />
      </div>

      <div class="actions">
        <button
          ref="cancelButton"
          type="button"
          class="ghost"
          data-test="confirm-cancel"
          @click="declinePurchase()"
        >
          Cancel
        </button>
        <button type="button" class="accept" data-test="confirm-accept" @click="acceptPurchase()">
          {{ purchaseRequest.confirmLabel ?? 'Buy' }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/*
 * Over everything, including the hangar's canvas and the customise window. The
 * z-index is deliberately the highest in the app: a confirmation that opened
 * *behind* the panel that raised it would be a modal nobody can answer.
 */
.scrim {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: grid;
  place-items: center;
  padding: 1rem;
  background: rgba(4, 6, 9, 0.68);
  backdrop-filter: blur(3px);
}

/*
 * A column of three: the head (picture and words), the stat comparison, the
 * buttons.
 *
 * **Flex rather than a grid, and that is what buys "no scroll".** The dialog
 * itself never scrolls — `overflow: hidden` — and exactly one child is allowed
 * to give way under a short viewport: `.delta`, the stat list. So the question,
 * the price and the two buttons are on screen at every size, which is the
 * failure the old `overflow-y: auto` produced and the one a confirmation can
 * least afford: a dialog whose *Cancel* is below the fold. A grid row cannot be
 * told to shrink without naming the rows, and two of the three are conditional.
 *
 * In the common case nothing scrolls at all — only an upgrade carries stats, and
 * `PurchaseRequest.stats` is deliberately unset for buying a hull or a turret
 * outright.
 */
.dialog {
  display: flex;
  flex-direction: column;
  /* A shade wider than the text-only dialog was, so a 8/5 still and a row of stat
     bars both fit without either being cropped to fit a width chosen for a
     sentence. Still narrow enough to read as a modal rather than a page. */
  width: min(21rem, 100%);
  max-height: calc(100dvh - 2rem);
  overflow: hidden;
  padding: 1.1rem 1.2rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 5px;
  background: var(--steel-800);
  box-shadow: 0 1.2rem 3rem rgba(0, 0, 0, 0.6);
}

/*
 * The picture and the words, stacked by default and side by side when there is a
 * picture worth standing a column on.
 */
.head {
  display: grid;
  flex: none;
  grid-template-columns: minmax(0, 1fr);
}

/*
 * ─── The two-column shape ────────────────────────────────────────────────────
 *
 * **The horizontal card is what makes the research dialog fit a landscape
 * phone.** Stacked, a 8/5 still plus four lines of type plus the buttons is
 * taller than a 390px viewport, and the dialog scrolled — with the confirm
 * button under the fold. Beside each other, the height is the taller of a
 * 7.8rem picture and ~5.5rem of words, and the whole thing lands around 13rem.
 *
 * The art column is a fixed 12.5rem rather than a fraction, so the picture is
 * the same size whatever the words happen to say. A `1fr 1fr` would let a long
 * item name shrink the still, which is the one thing on the dialog with a fixed
 * aspect ratio to respect.
 *
 * `align-items: start` so a short words column does not stretch its price line
 * down the height of the picture beside it.
 */
.dialog.wide {
  width: min(34rem, 100%);
}

.dialog.wide .head {
  grid-template-columns: 12.5rem minmax(0, 1fr);
  column-gap: 1.1rem;
  align-items: start;
}

/* Stacked, the picture keeps the gap it always had below it; side by side that
   gap is the grid's, and a bottom margin would only push the column down. */
.dialog.wide .art {
  margin-bottom: 0;
}

/*
 * The eyebrow is the dialog's own header, above both columns rather than inside
 * the words column — so a title badge, which stays stacked, keeps the exact
 * order it always had (eyebrow, badge, question) and the two-column shape gets a
 * header across the top instead of one hanging over the right-hand half.
 */
.dialog h2 {
  flex: none;
  margin: 0 0 0.7rem;
  color: var(--steel-400);
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.question {
  margin: 0;
  font-size: 1.05rem;
  line-height: 1.35;
}

.question strong {
  color: var(--accent);
}

.detail {
  margin: 0.25rem 0 0;
  color: var(--steel-400);
  font-size: 0.78rem;
}

/*
 * The picture. Fixed 8/5 box — the catalogue cards' own aspect — so a hull still
 * is framed here exactly as it was on the card that was clicked, rather than
 * being letterboxed differently by a modal with its own idea of shape.
 */
.art {
  position: relative;
  aspect-ratio: 8 / 5;
  margin: 0 0 0.85rem;
  border: 1px solid var(--steel-700);
  border-radius: 4px;
  /* Light rather than the plate's own dark ground — a still or an icon drawn
     for a pale studio backdrop (the parts and research renders) reads correctly
     against it, where the dark plate showed them as a silhouette. */
  background: #d1d1d1;
  overflow: hidden;
}

/* Not a title's: its badge carries small emblem images of its own, and this
   rule would stretch them into a picture behind the badge. */
.art:not(.badge) :deep(img),
.art > img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  /* Cover for a pattern or a photograph, which are meant to fill the plate;
     `contain` for the part and research icons, which are objects on a
     transparent ground and would be cropped into abstraction by cover. */
  object-fit: cover;
}

.art > img.contain {
  object-fit: contain;
  padding: 0.6rem;
}

/*
 * A title's box is not a photograph's box.
 *
 * The 8/5 plate is sized for a hull still; a badge is one short line of drawn
 * text, and holding it in the same rectangle leaves it floating in a field of
 * empty ground. So this kind drops the aspect ratio and takes the badge's own
 * height plus a little air — the only art kind whose subject has no picture
 * dimensions to respect.
 */
.art.badge {
  aspect-ratio: auto;
  padding: 1.1rem 0;
  /* The dialog's own dark ground: the light plate is for renders, and a badge
     is drawn for the dark nameplate it is worn on. */
  background: rgba(0, 0, 0, 0.28);
}

.badge-art {
  display: grid;
  place-items: center;
}

/*
 * The stat comparison, held to the same inset as the rest of the dialog body.
 *
 * **The one child allowed to give way.** `min-height: 0` is what lets a flex
 * item shrink below its content at all, and `flex: 0 1 auto` says it takes its
 * own height when there is room and gives it back when there is not. Everything
 * else in the dialog — the question, the price, the buttons — is `flex: none` by
 * consequence of never being told otherwise, so a short viewport takes height
 * from the bars and never from the decision.
 */
.delta {
  flex: 0 1 auto;
  min-height: 0;
  overflow-y: auto;
  margin: 0 0 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--steel-700);
}

/* The number is the point of the dialog, so it is the largest thing in it. */
.price {
  display: flex;
  align-items: baseline;
  gap: 0.3rem;
  margin: 0.9rem 0 1.1rem;
}

.amount {
  font-size: 1.5rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

/*
 * The unit sits at 1.15rem against a 1.5rem figure, not the 0.8rem the written
 * `cr` used.
 *
 * A text unit *should* be quieter than the number — it is a label. A mark is
 * not a label, it is the other half of the amount, and at 0.8rem it read as a
 * speck dropped beside the headline rather than as the currency the headline is
 * in. Still smaller than the figure, because the rule this dialog is built on is
 * that the number paid is the loudest thing on it.
 *
 * `align-items: baseline` on `.price` does the rest — `CurrencyIcon` carries its
 * own baseline shift, so the mark sits on the figure's baseline rather than on
 * its centre.
 */
.unit {
  color: var(--steel-400);
  font-size: 1.15rem;
}

/* `flex: none` stated rather than relied on: these two buttons are the whole
   point of the dialog and must never be the thing that gives way. */
.actions {
  display: flex;
  flex: none;
  justify-content: flex-end;
  gap: 0.5rem;
}

.ghost {
  padding: 0.5rem 0.9rem;
  border: 1px solid var(--steel-600);
  border-radius: 3px;
  background: transparent;
  color: var(--steel-200);
  font-size: 0.82rem;
  cursor: pointer;
  transition:
    border-color 120ms ease-out,
    color 120ms ease-out,
    background 120ms ease-out;
}

.ghost:hover {
  border-color: var(--steel-400, #9aa5b1);
  color: #fff;
}

.ghost:active {
  background: rgba(255, 255, 255, 0.05);
}

.ghost:focus-visible {
  outline: 1px solid var(--steel-400, #9aa5b1);
  outline-offset: 1px;
}

/*
 * The commit, so it is the one button on the loudest colour on the screen and
 * the only one that lifts — a purchase is the single moment on this dialog
 * worth a bit of weight under the pointer.
 */
.accept {
  padding: 0.5rem 1.3rem;
  border: 1px solid var(--accent);
  border-radius: 3px;
  background: var(--accent);
  color: #14181d;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  transition:
    transform 120ms ease-out,
    box-shadow 120ms ease-out,
    background 120ms ease-out;
}

.accept:hover {
  background: color-mix(in srgb, var(--accent) 88%, white 12%);
  box-shadow: 0 0.3rem 0.7rem rgba(0, 0, 0, 0.35);
  transform: translateY(-1px);
}

.accept:active {
  background: color-mix(in srgb, var(--accent) 85%, black 15%);
  box-shadow: none;
  transform: translateY(0);
}

.accept:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 2px;
}

/*
 * ─── Short viewports ────────────────────────────────────────────────────────
 *
 * The garage's own `(max-height: 520px)` — a landscape phone. The two-column
 * shape above is most of the fix; this is the rest of it, and it takes height
 * from the picture and the padding rather than from the type, which still has to
 * be read at arm's length. The stat list keeps its own scroll for the one case
 * (an upgrade with a long comparison) that still cannot fit.
 */
@media (max-height: 520px) {
  .dialog {
    padding: 0.85rem 0.95rem 0.8rem;
  }

  .dialog.wide {
    width: min(31rem, 100%);
  }

  .dialog.wide .head {
    grid-template-columns: 10.5rem minmax(0, 1fr);
  }

  .dialog h2 {
    margin-bottom: 0.45rem;
  }

  .price {
    margin: 0.55rem 0 0.75rem;
  }

  .delta {
    margin-bottom: 0.7rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ghost,
  .accept {
    transition: none;
  }

  .accept:hover {
    transform: none;
  }
}
</style>
